import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Button, CircularProgress, Alert, Stack,
  Table, TableHead, TableRow, TableCell, TableBody, Chip, TextField, Divider,
  Accordion, AccordionSummary, AccordionDetails, InputAdornment, Tooltip
} from '@mui/material';
import { PlayArrow, Preview, ExpandMore, Receipt, CurrencyExchange, Info } from '@mui/icons-material';
import api from '../../services/api';

const fmt = (n: any) => Number(n || 0).toFixed(2);
const TYPE_COLOR: any = { flat_fee: 'primary', bundle: 'success', per_seat: 'info', per_agent: 'secondary', overage: 'error', usage: 'warning' };
const TYPE_LABEL: any = { flat_fee: 'Flat Fee', bundle: 'Bundle', per_seat: 'Per Seat', per_agent: 'Per Agent', overage: 'Overage', usage: 'Usage' };

// Recalculate a tenant's subtotal from its current lines
function recalcTenant(tenant: any): any {
  const subtotal_aed = tenant.lines.reduce((s: number, l: any) => s + (l.amount_aed || 0), 0);
  return { ...tenant, subtotal_aed: parseFloat(subtotal_aed.toFixed(4)) };
}

// Recalculate a line's AED amount when qty changes
function recalcLine(line: any, newQty: number, rate: number): any {
  const baseAed = line.currency === 'USD'
    ? line.unit_price * rate
    : line.unit_price;
  const amount_aed = parseFloat((baseAed * newQty).toFixed(4));
  return { ...line, quantity: newQty, amount_aed };
}

export const BillingRunPage: React.FC = () => {
  const now = new Date();
  const [periodStart, setPeriodStart] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const [periodEnd, setPeriodEnd] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`);
  const [exchangeRate, setExchangeRate] = useState('3.67');
  const [tenants, setTenants] = useState<any[]>([]);   // editable preview state
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [runningId, setRunningId] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/subscriptions/settings').then(r => {
      if (r.data?.usd_aed_rate?.value) setExchangeRate(r.data.usd_aed_rate.value);
    });
  }, []);

  const loadPreview = async () => {
    setLoading(true); setError(''); setResult(null);
    try {
      const { data } = await api.post('/subscriptions/billing-preview', {
        period_start: periodStart, period_end: periodEnd,
      });
      setTenants(data.tenants || []);
    } catch (e: any) { setError(e.response?.data?.message || 'Preview failed'); }
    finally { setLoading(false); }
  };

  const updateExchangeRate = async (val: string) => {
    setExchangeRate(val);
    await api.patch('/subscriptions/settings/usd_aed_rate', { value: val });
    // Recalculate all USD lines with new rate
    const rate = parseFloat(val) || 3.67;
    setTenants(prev => prev.map(t => {
      const lines = t.lines.map((l: any) =>
        l.currency === 'USD' ? recalcLine(l, l.quantity, rate) : l
      );
      return recalcTenant({ ...t, lines });
    }));
  };

  const updateQty = (tenantId: string, lineIdx: number, newQty: number) => {
    const rate = parseFloat(exchangeRate) || 3.67;
    setTenants(prev => prev.map(t => {
      if (t.tenant_id !== tenantId) return t;
      const lines = t.lines.map((l: any, i: number) =>
        i === lineIdx ? recalcLine(l, newQty, rate) : l
      );
      return recalcTenant({ ...t, lines });
    }));
  };

  const runForTenant = async (tenantId?: string) => {
    const targets = tenantId
      ? tenants.filter(t => t.tenant_id === tenantId)
      : tenants;

    if (!targets.length) return;
    const confirmMsg = tenantId
      ? `Generate invoice for ${targets[0].tenant_name}?`
      : `Generate invoices for ${targets.length} customer(s)?`;
    if (!window.confirm(confirmMsg)) return;

    if (tenantId) setRunningId(tenantId); else setRunning(true);
    setError('');
    try {
      const { data } = await api.post('/subscriptions/billing-run-lines', {
        period_start: periodStart,
        period_end: periodEnd,
        tenant_lines: targets,
      });
      setResult(data);
      // Remove generated tenants from preview
      const generatedIds = new Set(data.invoices.map((i: any) => i.tenant_id));
      setTenants(prev => prev.filter(t => !generatedIds.has(t.tenant_id)));
    } catch (e: any) { setError(e.response?.data?.message || 'Billing run failed'); }
    finally { setRunning(false); setRunningId(null); }
  };

  const totalAed = tenants.reduce((s, t) => s + t.subtotal_aed, 0);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <Receipt sx={{ color: 'primary.main', fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>Monthly Billing Run</Typography>
          <Typography variant="body2" color="text.secondary">Generate invoices from active subscriptions — edit quantities before generating</Typography>
        </Box>
      </Box>

      {/* ── Controls ── */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
            <TextField size="small" type="date" label="Period Start" value={periodStart}
              onChange={e => setPeriodStart(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 160 }} />
            <TextField size="small" type="date" label="Period End" value={periodEnd}
              onChange={e => setPeriodEnd(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 160 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CurrencyExchange sx={{ color: 'text.secondary', fontSize: 18 }} />
              <TextField size="small" type="number" label="1 USD ="
                value={exchangeRate}
                onChange={e => updateExchangeRate(e.target.value)}
                InputProps={{ endAdornment: <InputAdornment position="end">AED</InputAdornment> }}
                sx={{ width: 140 }} inputProps={{ step: '0.01' }} />
            </Box>
            <Button variant="contained" startIcon={loading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <Preview />}
              onClick={loadPreview} disabled={loading}>
              Preview Invoices
            </Button>
            {tenants.length > 0 && (
              <Button variant="contained" color="success"
                startIcon={running ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <PlayArrow />}
                onClick={() => runForTenant()} disabled={running || !!runningId}>
                Generate All ({tenants.length})
              </Button>
            )}
          </Stack>

          {tenants.length > 0 && (
            <Alert severity="info" icon={<Info />} sx={{ mt: 2, py: 0.5 }}>
              Edit <strong>Qty</strong> on any line to bill multiple months at once — amounts update live.
              Backdating is supported — just set any past period above.
            </Alert>
          )}
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {result && (
        <Alert severity="success" sx={{ mb: 2 }}>
          ✓ {result.created} invoice{result.created !== 1 ? 's' : ''} generated —{' '}
          {result.invoices?.map((i: any) => `${i.tenant_name}: ${fmt(i.total_aed)} AED`).join(' · ')}
        </Alert>
      )}

      {/* ── Preview with editable quantities ── */}
      {tenants.length > 0 && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              {tenants.length} customer{tenants.length !== 1 ? 's' : ''} · Total: {fmt(totalAed)} AED · Rate: 1 USD = {exchangeRate} AED
            </Typography>
          </Box>

          <Stack spacing={1.5}>
            {tenants.map((tenant: any) => (
              <Accordion key={tenant.tenant_id} defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}
                  sx={{ bgcolor: 'grey.50', '&:hover': { bgcolor: 'grey.100' } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', pr: 2 }}>
                    <Typography fontWeight={700}>{tenant.tenant_name}</Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography fontFamily="monospace" fontWeight={700} color="primary.main">
                        {fmt(tenant.subtotal_aed)} AED
                      </Typography>
                      <Button size="small" variant="contained" color="success"
                        startIcon={runningId === tenant.tenant_id ? <CircularProgress size={14} sx={{ color: 'white' }} /> : <PlayArrow />}
                        onClick={e => { e.stopPropagation(); runForTenant(tenant.tenant_id); }}
                        disabled={running || !!runningId}>
                        Generate
                      </Button>
                    </Stack>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                        <TableCell sx={{ fontWeight: 700, width: 110 }}>
                          <Tooltip title="Edit to bill multiple months (e.g. 3 = 3 months)">
                            <span>Qty ✏</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Unit Price</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Currency</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>AED Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tenant.lines.map((line: any, idx: number) => (
                        <TableRow key={idx} sx={{ '&:nth-of-type(even)': { bgcolor: '#fafafa' } }}>
                          <TableCell>{line.description}</TableCell>
                          <TableCell>
                            <Chip size="small" label={TYPE_LABEL[line.type] || line.type} color={TYPE_COLOR[line.type] || 'default'} />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              type="number"
                              value={line.quantity}
                              onChange={e => updateQty(tenant.tenant_id, idx, Math.max(1, parseInt(e.target.value) || 1))}
                              inputProps={{ min: 1, step: 1, style: { width: 60, textAlign: 'center' } }}
                              sx={{ '& .MuiOutlinedInput-root': { borderColor: line.quantity > 1 ? 'warning.main' : undefined } }}
                            />
                            {line.quantity > 1 && (
                              <Typography variant="caption" color="warning.main" display="block">
                                {line.quantity} months
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{fmt(line.unit_price)}</TableCell>
                          <TableCell>
                            <Chip size="small" label={line.currency} color={line.currency === 'USD' ? 'success' : 'primary'} />
                            {line.exchange_rate && (
                              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>×{line.exchange_rate}</Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, color: line.quantity > 1 ? 'warning.dark' : 'inherit' }}>
                            {fmt(line.amount_aed)} AED
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell colSpan={5} align="right" sx={{ fontWeight: 700 }}>Subtotal</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, color: 'primary.main' }}>
                          {fmt(tenant.subtotal_aed)} AED
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        </>
      )}

      {!loading && tenants.length === 0 && !result && (
        <Card sx={{ p: 6, textAlign: 'center' }}>
          <Typography color="text.secondary">
            Set a billing period and click <strong>Preview Invoices</strong> to see what will be generated.
          </Typography>
        </Card>
      )}
    </Box>
  );
};
